import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { removeLoadingKey } from '../../helpers/common'
import { toast } from 'react-toastify'

export const ForecastPlanningLoadingKeys = {
    getPlannedForecastsOptions: "GET_ALL_PLANNED_FORECASTS_OPTIONS",
    getPlannedForecasts: "GET_ALL_PLANNED_FORECASTS",
    getActualsByYear: "GET_ACTUALS_BY_YEAR",
    updatePlannedForecast: "UPDATE_PLANNED_FORECAST",
    duplicatePlannedForecast: "DUPLICATE_PLANNED_FORECAST",
    deletePlannedForecast: "DELETE_PLANNED_FORECAST",
    partialUpdate: "PARTIAL_UPDATE",
}

const initialState = {
    loading: false,
    plannedForecastsOptions: [],
    plannedActuals: [],
    plannedForecasts: [],
    loadingKeys: [],
    firstLoadComplete: false,
    error: null,
}

export const getActualsByYear = createAsyncThunk(
    'forecastPlanning/getActualsByYear',
    async ({ year }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/actuals-forecast-planning', {
                params: {
                    year
                }
            });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const getPlannedForecastsOptions = createAsyncThunk(
    'forecastPlanning/getPlannedForecastsOptions',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/forecast-planning-options');
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const getPlannedForecasts = createAsyncThunk(
    'forecastPlanning/getPlannedForecasts',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/forecast-planning');
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const duplicatePlannedForecastRequest = createAsyncThunk(
    'forecastPlanning/duplicatePlannedForecastRequest',
    async ({
        forecastPlanningId,
        name,
        options
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/forecast-planning/duplicate', { 
                forecastPlanningId,
                name,
                options
            });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const updatePlannedForecastRequest = createAsyncThunk(
    'forecastPlanning/updatePlannedForecastRequest',
    async ({
        forecastPlanningId,
        budgetId,
        name,
        note,
        lastType,
        actualPlans,
        fromSave,
        editedData
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/forecast-planning/upsert', { 
                forecastPlanningId, 
                budgetId,
                name, 
                note, 
                actualPlans,
                lastType,
                fromSave,
                editedData
            });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const partialUpdateForecastPlanningRequest = createAsyncThunk(
    'forecastPlanning/partialUpdateForecastPlanningRequest',
    async ({ forecastPlanningId, lastType, scenarioChanges, topLevelChanges }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/forecast-planning/partial-update', {
                forecastPlanningId,
                lastType,
                scenarioChanges,
                topLevelChanges,
            });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const deletePlannedForecastRequest = createAsyncThunk(
    'forecastPlanning/deletePlannedForecastRequest',
    async ({
        id
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.delete(`/api/v1/forecast-planning/delete/${id}`);
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const forecastPlanningSlice = createSlice({
    name: 'forecastPlanning',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getActualsByYear.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastPlanningLoadingKeys.getActualsByYear];
            })
            .addCase(getActualsByYear.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.plannedActuals = action.payload.items;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(ForecastPlanningLoadingKeys.getActualsByYear, state.loadingKeys);
            })
            .addCase(getActualsByYear.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.plannedActuals = [];
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(ForecastPlanningLoadingKeys.getActualsByYear, state.loadingKeys);
            })
            .addCase(getPlannedForecastsOptions.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastPlanningLoadingKeys.getPlannedForecastsOptions];
            })
            .addCase(getPlannedForecastsOptions.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.plannedForecastsOptions = action.payload.items;
                state.loadingKeys = removeLoadingKey(ForecastPlanningLoadingKeys.getPlannedForecastsOptions, state.loadingKeys);
            })
            .addCase(getPlannedForecastsOptions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.plannedForecastsOptions = [];
                state.loadingKeys = removeLoadingKey(ForecastPlanningLoadingKeys.getPlannedForecastsOptions, state.loadingKeys);
            })
            .addCase(getPlannedForecasts.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastPlanningLoadingKeys.getPlannedForecasts];
            })
            .addCase(getPlannedForecasts.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.plannedForecasts = action.payload.items;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(ForecastPlanningLoadingKeys.getPlannedForecasts, state.loadingKeys);
            })
            .addCase(getPlannedForecasts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.plannedForecasts = [];
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(ForecastPlanningLoadingKeys.getPlannedForecasts, state.loadingKeys);
            })
            .addCase(duplicatePlannedForecastRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastPlanningLoadingKeys.duplicatePlannedForecast];
            })
            .addCase(duplicatePlannedForecastRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.loadingKeys = removeLoadingKey(ForecastPlanningLoadingKeys.duplicatePlannedForecast, state.loadingKeys);
                toast.success(`Planned forecast duplicated successfully.`, { position: "bottom-right" })
            })
            .addCase(duplicatePlannedForecastRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(ForecastPlanningLoadingKeys.duplicatePlannedForecast, state.loadingKeys);
                toast.error(
                    `Failed to duplicate planned forecast. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(updatePlannedForecastRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastPlanningLoadingKeys.updatePlannedForecast];
            })
            .addCase(updatePlannedForecastRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.loadingKeys = removeLoadingKey(ForecastPlanningLoadingKeys.updatePlannedForecast, state.loadingKeys);
                toast.success(`Planned forecast update successfully.`, { position: "bottom-right" })
            })
            .addCase(updatePlannedForecastRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(ForecastPlanningLoadingKeys.updatePlannedForecast, state.loadingKeys);
                toast.error(
                    `Failed to update planned forecast. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(deletePlannedForecastRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastPlanningLoadingKeys.deletePlannedForecast];
            })
            .addCase(deletePlannedForecastRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.loadingKeys = removeLoadingKey(ForecastPlanningLoadingKeys.deletePlannedForecast, state.loadingKeys);
                toast.success(`Planned forecast deleted successfully.`, { position: "bottom-right" })
            })
            .addCase(deletePlannedForecastRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(ForecastPlanningLoadingKeys.deletePlannedForecast, state.loadingKeys);
                toast.error(
                    `Planned forecast can't be deleted. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(partialUpdateForecastPlanningRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastPlanningLoadingKeys.partialUpdate];
            })
            .addCase(partialUpdateForecastPlanningRequest.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
                state.loadingKeys = removeLoadingKey(ForecastPlanningLoadingKeys.partialUpdate, state.loadingKeys);
                toast.success(`Forecast updated.`, { position: "bottom-right" })
            })
            .addCase(partialUpdateForecastPlanningRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(ForecastPlanningLoadingKeys.partialUpdate, state.loadingKeys);
                toast.error(`Failed to update forecast.`, { position: "bottom-right" })
            })
    }
})
export const {} = forecastPlanningSlice.actions;
export default forecastPlanningSlice.reducer 